import { createContext, createSignal, useContext, type JSX } from 'solid-js';
import type {
  ProjectDataRevealContextValue,
  ProjectDataRevealProviderProps,
  ProjectDataRevealTarget,
} from './project-data-reveal.types';

const EMPTY_TARGETS: readonly ProjectDataRevealTarget[] = [];

const NOOP_REVEAL: ProjectDataRevealContextValue = {
  targetsFor: () => EMPTY_TARGETS,
  isPending: () => false,
  markApplied: () => undefined,
};

const ProjectDataRevealContext =
  createContext<ProjectDataRevealContextValue>(NOOP_REVEAL);

function toTargetKey(target: ProjectDataRevealTarget): string {
  return target.focus === undefined
    ? `${target.section}|${target.ref}`
    : `${target.section}|${target.ref}|${target.focus}`;
}

export function ProjectDataRevealProvider(
  props: ProjectDataRevealProviderProps
): JSX.Element {
  // Reveal targets are fixed for the session from the URL; do not track prop changes.
  // eslint-disable-next-line solid/reactivity
  const allTargets = props.targets;

  const targetsBySection = new Map<string, ProjectDataRevealTarget[]>();
  for (const target of allTargets) {
    const existing = targetsBySection.get(target.section);
    if (existing) {
      existing.push(target);
    } else {
      targetsBySection.set(target.section, [target]);
    }
  }

  const [pendingKeys, setPendingKeys] = createSignal(
    new Set(allTargets.map(toTargetKey))
  );

  const value: ProjectDataRevealContextValue = {
    targetsFor: (section) => targetsBySection.get(section) ?? EMPTY_TARGETS,
    isPending: (target) => pendingKeys().has(toTargetKey(target)),
    markApplied: (target) => {
      const key = toTargetKey(target);
      setPendingKeys((prev) => {
        if (!prev.has(key)) {
          return prev;
        }
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    },
  };

  return (
    <ProjectDataRevealContext.Provider value={value}>
      {props.children}
    </ProjectDataRevealContext.Provider>
  );
}

export function useProjectDataReveal(): ProjectDataRevealContextValue {
  return useContext(ProjectDataRevealContext);
}
